/*
This serves two functions:
1. Record every action and state transition in the SPA. This is useful for debugging.
2. Send every error to Sentry. This allows us to guarantee that we record every error
   from the storage adapter, and then by the Client.
*/
import {
  actionMessage,
  unconfirmedActionMessage,
  type ActionMessage,
  type IncomingActionMessage,
  type IncomingUnconfirmedActionMessage,
  type Reducer,
  type UnconfirmedActionMessage,
} from "@engine/core";
import type { RoundDownload } from "./downloadedRound";
import type { ActionError, ClientStorageAdapter } from "@engine/network";
import { type NumberOrRange } from "@engine/network";
import type { RoundAggregate } from "@engine/round/aggregate";
import {
  type ClientSideEffectWriter,
  type InfoStoreReader,
} from "@engine/round/infoStore";
import { type Track } from "@lib/types";
import * as Sentry from "@lib/telemetry/sentry";
import Dexie from "dexie";
import { z } from "zod";
import { captureExceptionWithPriority } from "@lib/telemetry/sentry";
import { recentDurabilityFailures } from "@lib/offline/durabilityFailureLog";
import { StallError } from "@lib/utils/stallGuard";
import {
  type CacheFirstMissReason,
  type OfflineCacheConfig,
  type RoundLoadMode,
  SPAStorageAdapter,
} from "./SPAStorageAdapter";

const _actionRecord = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("construct"),
    aggregateId: z.string(),
    userProfileHash: z.string(),
    lastConfirmedAggVersion: z.number(),
  }),
  z.object({
    type: z.literal("performUnconfirmedAction"),
    action: unconfirmedActionMessage,
    error: z.instanceof(Error).optional(),
  }),
  z.object({
    type: z.literal("performConfirmedAction"),
    action: actionMessage,
    error: z.instanceof(Error).optional(),
    actionErrors: z
      .array(
        z.object({
          actionId: z.string(),
          error: z.instanceof(Error),
        }),
      )
      .optional(),
  }),
]);

type ActionRecord = z.infer<typeof _actionRecord>;

export default class SPAStorageAdapterWrapper implements ClientStorageAdapter {
  storageAdapter: SPAStorageAdapter;
  tape: ActionRecord[];

  constructor(
    storageAdapter: SPAStorageAdapter,
    getAggregate?: (
      roundId: string,
    ) => Promise<RoundDownload | "round-member-not-found">,
  ) {
    this.storageAdapter = storageAdapter;
    this.tape = [
      {
        type: "construct",
        aggregateId: this.storageAdapter.aggregateId,
        userProfileHash: this.storageAdapter.userProfileHash,
        lastConfirmedAggVersion: this.storageAdapter.getLastConfirmedVersion(),
      },
    ];
    if (typeof window !== "undefined") {
      const debugWindow = window as Window & {
        getSpaState?: () => Promise<unknown>;
        getTape?: () => ActionRecord[];
        getAggregate?: () => Promise<unknown>;
      };
      debugWindow.getSpaState = () => this._getState(true);
      debugWindow.getTape = () => this.tape;
      if (getAggregate) {
        debugWindow.getAggregate = () =>
          getAggregate(this.storageAdapter.aggregateId);
      }
    }
  }

  pushTape(actionRecord: ActionRecord) {
    this.tape.push(actionRecord);
    if (this.tape.length > 100) {
      this.tape.shift();
    }
  }

  async clearUnconfirmedActions() {
    await this.storageAdapter.clearUnconfirmedActions();
  }

  async removeUnconfirmedAction(reducer: Reducer, actionId: string) {
    await this.storageAdapter.removeUnconfirmedAction(reducer, actionId);
  }

  async _getState(full: boolean = false) {
    const adapter = this.storageAdapter;
    const db = adapter.db;

    const state = {
      tape: this.tape,
      aggregateId: adapter.aggregateId,
      userProfileHash: adapter.userProfileHash,
      currentUnconfirmedAggVersion: adapter.currentUnconfirmedAgg.version,
      snapshotCount: adapter.confirmedSnapshotsSinceLoad.length,
      unconfirmedActions: await adapter.getUnconfirmedActions(),
      storageEstimate: undefined as StorageEstimate | undefined,
    };

    if (
      typeof window !== "undefined" &&
      window.navigator &&
      navigator.storage &&
      navigator.storage.estimate
    ) {
      state.storageEstimate = await navigator.storage.estimate();
    }

    const confirmedActions = adapter.confirmedActionsSinceLoad;

    const unconfirmedActions = db.unconfirmedActions2
      .where("aggregateId")
      .equals(adapter.aggregateId);

    if (full) {
      return {
        ...state,
        confirmedActions,
        unconfirmedActions: await unconfirmedActions.toArray(),
        lastConfirmedAgg: adapter.lastConfirmedAgg,
      };
    } else {
      return {
        ...state,
        confirmedActionCount: confirmedActions.length,
        unconfirmedActionCount: await unconfirmedActions.count(),
        lastConfirmedAgg: adapter.lastConfirmedAgg.version,
      };
    }
  }

  async captureException(error: Error) {
    Sentry.setContext("state", await this._getState());

    if (navigator.storage && navigator.storage.estimate) {
      const estimation = await navigator.storage.estimate();
      Sentry.setContext("storageEstimate", {
        quota: estimation.quota,
        usage: estimation.usage,
      });
    }

    if (error instanceof Dexie.AbortError) {
      // Event-local scope: contexts set globally persist onto every later
      // event, so an abort's diagnostics (its outer error, and the
      // durability ring below) would ride along on unrelated errors captured
      // afterwards. Scoping attaches them to THIS report only.
      Sentry.withScope((scope) => {
        scope.setContext("outerError", {
          message: error.message,
          stack: error.stack,
        });
        // The Lockdown stand-in aborts a transaction when a native file
        // write can't be made durable, and its synthetic abort carries NO
        // `inner` — the underlying cause is only in the durability ring.
        // Attach it so an offline-edit failure's report names the real
        // error (SP-2649/8N class) instead of just "Transaction aborted".
        const recentFailures = recentDurabilityFailures();
        if (recentFailures.length > 0) {
          scope.setContext("recentDurabilityFailures", {
            failures: recentFailures,
          });
        }
        if (error.inner instanceof Error) {
          captureExceptionWithPriority(error.inner);
        } else {
          Sentry.captureException(error.inner);
        }
      });
    } else {
      captureExceptionWithPriority(error);
    }
  }

  static async init(
    reducer: Reducer,
    aggregateId: string,
    userProfileHash: string,
    downloadRound: (aggregateId: string) => Promise<RoundDownload>,
    downloadActionsById: (
      aggregateId: string,
      ids: string[],
    ) => Promise<{ [id: string]: IncomingActionMessage }>,
    downloadActionsByVersion: (
      aggregateId: string,
      versions: NumberOrRange[],
    ) => Promise<{ [version: number]: IncomingActionMessage }>,
    infoStore: InfoStoreReader & ClientSideEffectWriter,
    viewerTrack: Track = "NO_TRACK",
    viewerUserId: string | null = null,
    getAggregate?: (
      roundId: string,
    ) => Promise<RoundDownload | "round-member-not-found">,
    offlineCache: OfflineCacheConfig = { enabled: false },
    ownerUserId?: string,
  ): Promise<SPAStorageAdapterWrapper> {
    try {
      const innerStorageAdapter = await SPAStorageAdapter.init(
        reducer,
        aggregateId,
        userProfileHash,
        downloadRound,
        downloadActionsById,
        downloadActionsByVersion,
        infoStore,
        viewerTrack,
        viewerUserId,
        offlineCache,
        ownerUserId,
      );
      return new SPAStorageAdapterWrapper(innerStorageAdapter, getAggregate);
    } catch (e) {
      Sentry.captureException(e);
      throw e;
    }
  }

  async performUnconfirmedAction(
    reducer: Reducer,
    action: UnconfirmedActionMessage,
  ): Promise<null | Error> {
    let error: Error | undefined;
    const result = await this.storageAdapter.performUnconfirmedAction(
      reducer,
      action,
    );
    if (result instanceof Error) {
      await this.captureException(result);
      error = result;
    }
    this.pushTape({
      type: "performUnconfirmedAction",
      action,
      error,
    });
    return result;
  }

  async performConfirmedAction(
    reducer: Reducer,
    action: ActionMessage,
  ): Promise<void | Error | ActionError[]> {
    let error: Error | undefined;
    let actionErrors: ActionError[] | undefined;
    const result = await this.storageAdapter.performConfirmedAction(
      reducer,
      action,
    );
    if (result instanceof Error) {
      // A StallError is the pump's flow-control signal (a catch-up fetch that
      // exhausted its timeout), not a storage fault: its reporting belongs to
      // the Client's stall-episode machinery, which latches to one Sentry
      // event per episode. Capturing it here would double-report the first
      // stall and fire an unlatched event for every re-delivered range whose
      // orphaned fetch later hits the timeout.
      if (!(result instanceof StallError)) {
        await this.captureException(result);
      }
      error = result;
    } else {
      actionErrors = result;
    }
    this.pushTape({
      type: "performConfirmedAction",
      action,
      error,
      actionErrors,
    });
    return result;
  }

  getConfirmedActions(): IncomingActionMessage[] {
    return this.storageAdapter.getConfirmedActions();
  }

  getAction(
    actionId: string,
  ): Promise<IncomingUnconfirmedActionMessage | null> {
    return this.storageAdapter.getAction(actionId);
  }

  getAggregateAtVersion(
    reducer: Reducer,
    version: number,
  ): Promise<RoundAggregate | null> {
    return this.storageAdapter.getAggregateAtVersion(reducer, version);
  }

  getUnconfirmedAggregateSync(): RoundAggregate {
    return this.storageAdapter.getUnconfirmedAggregateSync();
  }

  getConfirmedAggregateSync(): RoundAggregate {
    return this.storageAdapter.getConfirmedAggregateSync();
  }

  getLastConfirmedVersion(): number {
    return this.storageAdapter.getLastConfirmedVersion();
  }

  getStallDiagnostics(): Record<string, unknown> {
    return this.storageAdapter.getStallDiagnostics();
  }

  /** Queued edits that couldn't be replayed on the cached aggregate offline. */
  getUnshownOfflineEditCount(): number {
    return this.storageAdapter.unshownOfflineEditCount;
  }

  /**
   * How this load got its aggregate, for the round-open signal. The caller
   * reports it (rather than the adapter emitting) so the reported duration covers
   * the whole wait the funder sits through, not just the part inside init.
   */
  getLoadReport(): {
    loadMode: RoundLoadMode;
    cacheFirstEligible: boolean;
    cacheFirstGap: number | null;
    cacheFirstMissReason: CacheFirstMissReason | null;
    pendingEdits: {
      queuedCount: number;
      oldestQueuedAgeMs: number;
    } | null;
    unshownOfflineEditCount: number;
  } {
    return {
      loadMode: this.storageAdapter.loadMode,
      cacheFirstEligible: this.storageAdapter.cacheFirstEligible,
      cacheFirstGap: this.storageAdapter.cacheFirstGap,
      cacheFirstMissReason: this.storageAdapter.cacheFirstMissReason,
      pendingEdits: this.storageAdapter.pendingEditReport,
      unshownOfflineEditCount: this.storageAdapter.unshownOfflineEditCount,
    };
  }

  getUnconfirmedActions(): Promise<
    {
      createdAt: number;
      message: IncomingUnconfirmedActionMessage;
    }[]
  > {
    return this.storageAdapter.getUnconfirmedActions();
  }

  getQueuedFieldResponseDependencies(): Promise<string[]> {
    return this.storageAdapter.getQueuedFieldResponseDependencies();
  }

  checkConsistency(): Promise<void> {
    return this.storageAdapter.checkConsistency();
  }

  getAggregateBeforeUnconfirmedAction(
    reducer: Reducer,
    actionId: string,
  ): Promise<RoundAggregate | Error> {
    return this.storageAdapter.getAggregateBeforeUnconfirmedAction(
      reducer,
      actionId,
    );
  }
}
